import { useState } from 'react';
import toast from 'react-hot-toast';
import NicknameBar from '../components/NicknameBar';
import { useCountdown } from '../hooks/useCountdown';

interface PollOption {
  id: string;
  text: string;
}

interface PollVotingScreenProps {
  pollId: string;
  options: PollOption[];
  endsAt: number;
  serverTime: number;
  canVote: boolean;
  initialVotedOptionId?: string;
}

function PollVotingScreen({ pollId, options, endsAt, serverTime, canVote, initialVotedOptionId }: PollVotingScreenProps) {
  const remainingSeconds = useCountdown(endsAt, serverTime);
  const [votedOptionId, setVotedOptionId] = useState<string | null>(initialVotedOptionId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVote = async (optionId: string) => {
    if (!canVote || votedOptionId || isSubmitting) return;

    const audienceId = localStorage.getItem('audienceId');
    if (!audienceId) {
      toast.error('Sessione non valida');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/polls/${pollId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audienceId, optionId }),
      });

      if (response.ok) {
        setVotedOptionId(optionId);
        toast.success('Voto registrato!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore durante il voto');
      }
    } catch (error) {
      console.error('[PollVotingScreen] Error submitting vote:', error);
      toast.error('Errore di connessione');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen p-4 pt-10">
      <NicknameBar />
      {/* Timer */}
      <div className="text-center py-6">
        <div className={`text-7xl font-bold ${remainingSeconds <= 5 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
          {remainingSeconds}
        </div>
        <p className="text-ink-light mt-1 text-base">secondi rimasti</p>
      </div>

      {/* Opzioni */}
      <div className="flex-1 flex flex-col justify-center space-y-4 max-w-md mx-auto w-full">
        {options.map((option) => {
          const isVoted = votedOptionId === option.id;
          const isDisabled = !canVote || (votedOptionId !== null && !isVoted) || isSubmitting;

          return (
            <button
              key={option.id}
              onClick={() => handleVote(option.id)}
              disabled={isDisabled}
              className={`w-full py-5 px-6 rounded-xl text-xl font-bold transition-all ${
                isVoted
                  ? 'bg-parchment-400 text-ink ring-4 ring-parchment-300 scale-105'
                  : isDisabled
                  ? 'bg-parchment-300 text-ink-muted cursor-not-allowed'
                  : 'parchment-card text-ink hover:bg-parchment-200 active:scale-95'
              }`}
            >
              {option.text}
              {isVoted && <span className="ml-2">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Stato voto */}
      {votedOptionId && (
        <div className="text-center py-4">
          <p className="text-team-green font-semibold">Voto registrato!</p>
        </div>
      )}

      {!canVote && !votedOptionId && (
        <div className="text-center py-4">
          <p className="text-ink-light">Votazione in corso...</p>
        </div>
      )}
    </div>
  );
}

export default PollVotingScreen;
